import { useEffect, useRef, useState } from "react";

const STAR_COUNT = 180;

const StarsBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains("dark"));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const stars: { x: number; y: number; r: number; opacity: number; speed: number; phase: number }[] = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
    };
    resize();

    for (let i = 0; i < STAR_COUNT; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.2 + 0.3,
        opacity: Math.random() * 0.6 + 0.2,
        speed: Math.random() * 0.3 + 0.1,
        phase: Math.random() * Math.PI * 2,
      });
    }

    const drawStatic = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        const baseOpacity = isDark ? s.opacity : s.opacity * 0.18;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? `rgba(255,255,255,${baseOpacity})`
          : `rgba(0,0,0,${baseOpacity})`;
        ctx.fill();
      }
    };

    if (reducedMotion) {
      drawStatic();
      window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }

    let frame = 0;
    const draw = (t: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        const twinkle = Math.sin(t * 0.001 * s.speed + s.phase) * 0.3 + 0.7;
        const baseOpacity = isDark ? s.opacity : s.opacity * 0.18;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? `rgba(255,255,255,${baseOpacity * twinkle})`
          : `rgba(0,0,0,${baseOpacity * twinkle})`;
        ctx.fill();
      }
      frame = requestAnimationFrame(draw);
    };

    const start = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(draw);
    };
    const stop = () => {
      if (frame === 0) return;
      cancelAnimationFrame(frame);
      frame = 0;
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    start();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isDark]);

  return (
    <>
      {/* Nebula glow */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full blur-[120px]"
          style={{
            opacity: isDark ? 0.3 : 0.12,
            background: isDark
              ? "radial-gradient(ellipse at center, hsla(16,85%,45%,0.4) 0%, hsla(16,85%,30%,0.15) 40%, transparent 70%)"
              : "radial-gradient(ellipse at center, hsla(16,85%,55%,0.2) 0%, hsla(16,85%,50%,0.06) 40%, transparent 70%)",
          }}
        />
        <div
          className="absolute top-[20%] right-[10%] w-[400px] h-[400px] rounded-full blur-[100px]"
          style={{
            opacity: isDark ? 0.15 : 0.08,
            background: isDark
              ? "radial-gradient(ellipse at center, hsla(220,80%,50%,0.3) 0%, transparent 70%)"
              : "radial-gradient(ellipse at center, hsla(220,70%,60%,0.15) 0%, transparent 70%)",
          }}
        />
      </div>
      {/* Stars canvas */}
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-0"
        aria-hidden="true"
      />
    </>
  );
};

export default StarsBackground;
